import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';

export default function CommunityTab() {
  const [exports, setExports] = useState([]);
  const [selectedExport, setSelectedExport] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const loadExports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/community/exports');
      setExports(data);
      if (data.length > 0) {
        setSelectedExport(data[0]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading exports:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (pageNum) => {
    try {
      const offset = pageNum * LIMIT;
      const data = await apiCall(
        `/api/community/messages?export_id=${selectedExport.id}&limit=${LIMIT}&offset=${offset}`
      );
      setMessages(data);
      setPage(pageNum);
    } catch (err) {
      setError(err.message);
      console.error('Error loading messages:', err);
    }
  }, [selectedExport, LIMIT]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    loadExports();
  }, [loadExports]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    if (selectedExport) {
      loadMessages(0);
    }
  }, [selectedExport, loadMessages]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      loadMessages(page - 1);
    }
  };

  const handleNextPage = () => {
    if (messages.length >= LIMIT) {
      loadMessages(page + 1);
    }
  };

  const handleExportChange = (e) => {
    const expId = e.target.value;
    const exp = exports.find((ex) => ex.id === expId);
    if (exp) {
      setSelectedExport(exp);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="text-base sm:text-lg font-bold mb-2">Loading community chat...</div>
        <div className="text-sm text-on-surface-variant">Please wait</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-error-container p-3 sm:p-6 text-center">
        <p className="font-bold text-error mb-2 text-sm sm:text-base">Error Loading Community Chat</p>
        <p className="text-xs sm:text-sm text-on-error-container">{error}</p>
        <button onClick={loadExports} className="btn-primary mt-4 text-sm">
          Retry
        </button>
      </div>
    );
  }

  if (exports.length === 0) {
    return (
      <div className="card text-center py-8 sm:py-12 p-3 sm:p-4">
        <p className="text-2xl mb-2">💬</p>
        <p className="text-base sm:text-lg font-bold mb-2">No Community Chats Yet</p>
        <p className="text-xs sm:text-sm text-on-surface-variant mb-4">
          Send WhatsApp chat exports to:
          <br />
          <code className="bg-surface-container-low p-2 rounded inline-block mt-2 text-xs break-all">
            egac@themainhost.co.uk
          </code>
        </p>
        <p className="text-xs sm:text-sm text-on-surface-variant">
          Export your WhatsApp group chat as a ZIP file and email it to the address above.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Export selector */}
      <div className="card p-3 sm:p-4">
        <label className="text-xs sm:text-sm font-bold block mb-2">Select Chat Export</label>
        <select
          value={selectedExport?.id || ''}
          onChange={handleExportChange}
          className="w-full p-2 text-sm rounded border border-outline-variant bg-surface-container text-on-surface"
        >
          <option value="">-- Select an export --</option>
          {exports.map((exp) => (
            <option key={exp.id} value={exp.id}>
              {exp.group_name} — {exp.message_count} messages ({formatDate(exp.imported_at)})
            </option>
          ))}
        </select>
      </div>

      {selectedExport && (
        <>
          {/* Export info */}
          <div className="card p-3 sm:p-4 bg-surface-container-low">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
              <div>
                <p className="text-xs text-on-surface-variant">Messages</p>
                <p className="text-xl sm:text-2xl font-bold text-primary">{selectedExport.message_count}</p>
              </div>
              <div>
                <p className="text-xs text-on-surface-variant">Attachments</p>
                <p className="text-xl sm:text-2xl font-bold text-primary">{selectedExport.attachment_count}</p>
              </div>
              <div>
                <p className="text-xs text-on-surface-variant">First Message</p>
                <p className="text-xs sm:text-sm font-medium truncate">
                  {formatDate(selectedExport.first_message_date)}
                </p>
              </div>
              <div>
                <p className="text-xs text-on-surface-variant">Last Message</p>
                <p className="text-xs sm:text-sm font-medium truncate">
                  {formatDate(selectedExport.last_message_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Messages list */}
          {messages.length > 0 ? (
            <div className="space-y-2">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className="card p-2 sm:p-4 border-l-4 border-primary hover:bg-surface-container-high transition-colors"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-xs sm:text-base text-on-surface">{msg.author_name}</p>
                      <p className="text-xs text-on-surface-variant mt-0.5">
                        {formatDate(msg.message_time)}
                      </p>
                      <p className="text-xs sm:text-sm mt-2 whitespace-pre-wrap break-words text-on-surface">
                        {msg.message_text}
                      </p>
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-2 sm:mt-3 space-y-2">
                          {msg.attachments.map((att) => (
                            <div key={att.id} className="flex items-center gap-2">
                              {att.fileType === 'image' && att.url ? (
                                <div className="max-w-xs">
                                  <img
                                    src={att.url}
                                    alt={att.filename}
                                    className="rounded-lg max-h-32 sm:max-h-48 object-contain cursor-pointer hover:opacity-75 transition-opacity"
                                    onClick={() => window.open(att.url, '_blank')}
                                  />
                                  <p className="text-xs text-on-surface-variant mt-1 truncate">
                                    {att.filename}
                                  </p>
                                </div>
                              ) : att.url ? (
                                <div className="flex items-center gap-2 bg-surface-container-low p-2 rounded text-xs sm:text-sm">
                                  <span className="text-base flex-shrink-0">
                                    {att.fileType === 'video' ? '🎬' : '📄'}
                                  </span>
                                  <a
                                    href={att.url}
                                    download={att.filename}
                                    className="text-primary hover:text-primary-fixed underline font-medium truncate"
                                  >
                                    {att.filename}
                                  </a>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 bg-surface-container-low p-2 rounded text-xs opacity-50">
                                  <span className="text-base flex-shrink-0">❌</span>
                                  <p className="text-on-surface-variant truncate">
                                    {att.filename} (missing)
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card text-center py-8">
              <p className="text-on-surface-variant">No messages to display</p>
            </div>
          )}

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 py-4">
            <button
              onClick={handlePreviousPage}
              disabled={page === 0}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm rounded font-medium bg-primary-fixed text-on-primary-fixed disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-fixed/90 transition-colors w-full sm:w-auto"
            >
              <span className="hidden sm:inline">← Previous</span>
              <span className="sm:hidden">← Prev</span>
            </button>
            <span className="text-xs sm:text-sm font-medium text-center">
              Page {page + 1} <span className="hidden sm:inline">(showing {messages.length} of {selectedExport.message_count} messages)</span>
            </span>
            <button
              onClick={handleNextPage}
              disabled={messages.length < LIMIT}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm rounded font-medium bg-primary-fixed text-on-primary-fixed disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-fixed/90 transition-colors w-full sm:w-auto"
            >
              <span className="hidden sm:inline">Next →</span>
              <span className="sm:hidden">Next →</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
